import { Component, OnInit, EventEmitter, Output, Input, OnChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { readWrite } from '../../user/user-permission/user-permission.data';
import { UserPermissionService } from '../../user/user-permission/user-permission.service';
import { LogQueryResult, LogLevel, LogQueryParams, Log } from '../log.model';
import { LogService } from '../log.service';

@UntilDestroy()
@Component({
    selector: 'ifb-log-list',
    templateUrl: './log-list.component.html',
    styleUrls: ['./log-list.component.scss'],
    standalone: false
})
export class LogListComponent implements OnInit, OnChanges {
  data: LogListComponentData;
  queryParams: any;
  canReadWriteLog = false;
  expanded = [];
  tableColumns: string[] = ['log'];

  readonly LogLevel = LogLevel;

  @Input()
  expandAll: boolean;

  @Input()
  pageChanged: Observable<void>;

  @Output()
  archived = new EventEmitter<any>();

  @Output()
  filtered = new EventEmitter<LogQueryParams>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly logService: LogService,
    private readonly userPermissionService: UserPermissionService) {
  }

  ngOnInit(): void {
    this.route.data.pipe(untilDestroyed(this))
      .subscribe((it: LogListComponentData) => this.data = it);

    this.route.queryParams.pipe(untilDestroyed(this))
      .subscribe(it => { this.queryParams = it; });

    this.userPermissionService.granted([readWrite('admin-logs')])
      .subscribe(res => this.canReadWriteLog = res);

    this.pageChanged.pipe(untilDestroyed(this))
      .subscribe(() => this.expanded = []);
  }

  ngOnChanges(): void {
    if (this.data && this.data.logQueryResult && this.data.logQueryResult.totalCount)
      for (let index = 0; index < this.data.logQueryResult.values.length; index++) {
        this.expanded[index] = this.expandAll;
      }
  }

  archive(id: number): void {
    this.logService.archive(id).subscribe(() => {
      this.archived.emit();
    });
  }

  filter(property: string, value: string): void {
    const params: LogQueryParams = {};
    params[property] = value;
    this.filtered.emit(params);
  }

  substring(input: string): string {
    return (!input || input === '' || input.length < 8)
      ? input
      : (input.startsWith('"') ? input.substring(input.length - 8) : input.substring(input.length - 8));
  }

  detailsLink(type: string, id: number): void {
    this.router.navigate([`/${type}/${id}/profile`]);
  }

  logDetailsLink(id: number): string {
    return `/logs/${id}`;
  }

  getLevelIcon(level: any): string {
    switch (level) {
      case LogLevel.Fatal:
        return 'report';
      case LogLevel.Error:
        return 'error';
      case LogLevel.Warn:
        return 'warning';
      case LogLevel.Debug:
        return 'bug_report';
      case LogLevel.Info:
        return 'info';
      default:
        break;
    }
  }

  getLevelIconAvatarClass(level: LogLevel): string {
    switch (level) {
      case LogLevel.Fatal:
      case LogLevel.Error:
        return 'mat-red-icon';
      case LogLevel.Warn:
        return 'mat-orange-icon';
      case LogLevel.Debug:
        return 'mat-gray-icon';
      case LogLevel.Info:
        return 'mat-indigo-icon';
      default:
        break;
    }
  }

  hasException(log: Log): boolean {
    return log.level === LogLevel.Error && (log.exception && log.exception !== '');
  }

  hasMultipleLines(message: string): boolean {
    return message.includes('\n') || this.isJson(message);
  }

  toggle(i: number): void {
    this.expanded[i] = !this.expanded[i];
  }

  isJson(message: string): boolean {
    return message !== null && message.startsWith('{');
  }

  getJsonMessage(text: any): string {
    return this.isJson(text) ? JSON.parse(text) : text;
  }
}

export interface LogListComponentData {
  logQueryResult?: LogQueryResult;
}
